import { useState } from "react";
import { Search, ChevronDown, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

export default function IPOSearch({ ipos, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = ipos.filter((i) =>
    !query ||
    i.name.toLowerCase().includes(query.toLowerCase()) ||
    i.ticker.toLowerCase().includes(query.toLowerCase())
  );

  const handlePick = (i) => {
    onSelect(i);
    setOpen(false);
    setQuery("");
  };

  const handleCustom = () => {
    const name = query.trim();
    if (!name) return;
    onSelect({
      id: "custom-" + name.toLowerCase().replace(/\s+/g, "-"),
      name,
      ticker: name.toUpperCase().slice(0, 4),
      sector: "Custom / User-supplied",
      blurb: "",
    });
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group flex w-full items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.04] px-5 py-4 text-left transition-colors hover:border-white/30 hover:bg-white/[0.07]"
          data-testid="ipo-search-trigger"
        >
          <Search className="h-5 w-5 shrink-0 text-zinc-400" />
          <div className="min-w-0 flex-1">
            <div className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Step 1 · Select an IPO or private company
            </div>
            <div className="mt-0.5 truncate font-chivo text-lg font-bold text-white">
              {selected ? selected.name : "Type SpaceX, OpenAI, Stripe…"}
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-zinc-500 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[var(--radix-popover-trigger-width)] border-white/10 bg-[#09090B] p-0 text-white"
        data-testid="ipo-search-popover"
      >
        <div className="border-b border-white/10 p-3">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && filtered.length === 0) handleCustom(); }}
            placeholder="Search or type a custom company…"
            className="w-full border-0 bg-transparent font-outfit text-sm text-white placeholder:text-zinc-600 focus:outline-none"
            data-testid="ipo-search-input"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          {filtered.length === 0 ? (
            <button
              onClick={handleCustom}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-white/5"
              data-testid="ipo-search-custom"
            >
              <Sparkles className="h-4 w-4 text-indigo-300" />
              <div>
                <div className="text-sm font-medium">Use &ldquo;{query}&rdquo;</div>
                <div className="text-xs text-zinc-500">AI will research alternatives from scratch</div>
              </div>
            </button>
          ) : (
            filtered.map((i) => (
              <button
                key={i.id}
                onClick={() => handlePick(i)}
                className={`flex w-full items-start gap-3 rounded-lg p-3 text-left hover:bg-white/5 ${selected?.id === i.id ? "bg-white/5" : ""}`}
                data-testid={`ipo-option-${i.id}`}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/5 font-mono-jb text-[10px] font-bold text-white">
                  ${i.ticker}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-chivo text-sm font-bold text-white">{i.name}</div>
                  <div className="truncate text-xs text-zinc-400">{i.sector} · {i.blurb}</div>
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
